import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { enregistrerMouvement, updateProduit, deleteProduit } from '../lib/api'
import { verifierAlertes, activerNotifications, notificationsActivees } from '../lib/notifications'
import { enregistrerMouvementCaissier, deleteProduitCaissier } from '../lib/apiCaissier'

const CAT_EMOJI = { alimentation: '🍚', boissons: '🥤', hygiene: '🧴', epices: '🌶', conserves: '🥫', autres: '📦' }
const CATEGORIES = [
  { id: 'alimentation', label: '🍚 Alimentation' },
  { id: 'boissons',     label: '🥤 Boissons' },
  { id: 'hygiene',      label: '🧴 Hygiène' },
  { id: 'epices',       label: '🌶 Épices' },
  { id: 'conserves',    label: '🥫 Conserves' },
  { id: 'autres',       label: '📦 Autres' },
]

export default function Stock({ produits, onRefresh, boutiqueId: boutiqueIdProp }) {
  const { user, boutiqueId: boutiqueIdAuth } = useAuth()
  const { t, lang } = useLang()
  const isCaissier = !user && !!boutiqueIdProp
  const boutiqueId = boutiqueIdProp || boutiqueIdAuth

  const [search, setSearch] = useState('')
  const [moveModal, setMoveModal] = useState(null)
  const [editModal, setEditModal] = useState(null)
  const [qty, setQty] = useState(0.5)
  const [note, setNote] = useState('')
  const [deleteModal, setDeleteModal] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  // Vérifier alertes au chargement
  useState(() => {
    if (produits.length > 0) verifierAlertes(produits, lang)
  }, [produits])

  const filtered = produits.filter(p => p.nom.toLowerCase().includes(search.toLowerCase()))

  // ── MOUVEMENT ──────────────────────────────────────────────

  function badgePeremption(p, lg) {
    if (!p.est_perissable || !p.date_peremption) return null
    const diff = Math.ceil((new Date(p.date_peremption) - new Date()) / (1000 * 60 * 60 * 24))
    if (diff < 0)   return { label: lg === 'fr' ? 'Perime' : 'Expired',                              color: '#8F3A3A',        bg: 'var(--rose-pale)',   icon: '☠' }
    if (diff === 0) return { label: lg === 'fr' ? 'Expire auj.' : 'Exp. today',                      color: 'var(--rose)',    bg: 'var(--rose-pale)',   icon: '!' }
    if (diff <= 7)  return { label: lg === 'fr' ? 'Expire dans ' + diff + 'j' : 'Exp. in ' + diff + 'd', color: '#854F0B', bg: 'var(--amber-pale)', icon: '⚠' }
    if (diff <= 30) return { label: lg === 'fr' ? 'Expire dans ' + diff + 'j' : 'Exp. in ' + diff + 'd', color: 'var(--muted)', bg: 'var(--cream-dark)', icon: 'i' }
    return null
  }

  function openMove(produit, type, e) {
    e.stopPropagation()
    setMoveModal({ produit, type }); setQty(0.5); setNote(''); setErr('')
  }

  async function confirmMove() {
    setSaving(true); setErr('')
    const fn = isCaissier ? enregistrerMouvementCaissier : enregistrerMouvement
    const { error } = await fn(boutiqueId, {
      produitId: moveModal.produit.id, produitNom: moveModal.produit.nom,
      type: moveModal.type, quantite: qty, note
    })
    setSaving(false)
    if (error) { setErr(error.message); return }
    setMoveModal(null); onRefresh()
  }

  // ── ÉDITION PRODUIT ────────────────────────────────────────
  function openEdit(produit) {
    setEditModal({
      id: produit.id,
      nom: produit.nom,
      prix_achat: produit.prix_achat || '',
      prix_unitaire: produit.prix_unitaire || '',
      unite: produit.unite || '',
      seuil_alerte: produit.seuil_alerte || 5,
      categorie: produit.categorie || 'autres'
    })
    setErr('')
  }

  async function saveEdit() {
    setSaving(true); setErr('')
    const { error } = await updateProduit(editModal.id, {
      nom: editModal.nom,
      prix_achat: parseInt(editModal.prix_achat) || 0,
      prix_unitaire: parseInt(editModal.prix_unitaire) || 0,
      unite: editModal.unite,
      seuil_alerte: parseInt(editModal.seuil_alerte) || 5,
      categorie: editModal.categorie
    })
    setSaving(false)
    if (error) { setErr(error.message); return }
    setEditModal(null); onRefresh()
  }

  async function handleDelete() {
    if (!deleteModal) return
    setDeleting(true)
    const { error } = isCaissier
      ? await deleteProduitCaissier(boutiqueId, deleteModal.id)
      : await deleteProduit(deleteModal.id)
    setDeleting(false)
    if (error) { setErr(error.message); return }
    setDeleteModal(null)
    onRefresh()
  }

  const margeEdit = editModal?.prix_unitaire && editModal?.prix_achat
    ? parseInt(editModal.prix_unitaire) - parseInt(editModal.prix_achat)
    : null

  return (
    <div className="main">
      <div className="page-title">{t('my_stock')}</div>

      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input type="text" placeholder={t('search_product')}
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* HINT */}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: '1rem', textAlign: 'center' }}>
        {lang === 'fr' ? '💡 Clique sur un produit pour modifier ses informations' : '💡 Click on a product to edit its information'}
      </div>

      {filtered.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">📦</span>
          {search ? t('no_product_found') : t('no_product')}
        </div>
      ) : (
        filtered.map(p => {
          const cls = p.quantite === 0 ? 'stock-out' : p.quantite <= p.seuil_alerte ? 'stock-low' : 'stock-ok'
          const marge = p.prix_unitaire && p.prix_achat ? p.prix_unitaire - p.prix_achat : null
          return (
            <div key={p.id} className="product-item" onClick={() => openEdit(p)}
              onContextMenu={e => { e.preventDefault(); setErr(''); setDeleteModal(p) }}
              onTouchStart={e => {
                const timer = setTimeout(() => { setErr(''); setDeleteModal(p) }, 600)
                e.currentTarget._longpress = timer
              }}
              onTouchEnd={e => {
                clearTimeout(e.currentTarget._longpress)
              }}
              onTouchMove={e => {
                clearTimeout(e.currentTarget._longpress)
              }}
              style={{ cursor: 'pointer' }}>
              <div className="product-icon">{CAT_EMOJI[p.categorie] || '📦'}</div>
              <div className="product-info">
                <div className="product-name">{p.nom}</div>
                <div className="product-meta" style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {p.prix_achat > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--rose)' }}>
                      🛒 {p.prix_achat.toLocaleString()} FCFA
                    </span>
                  )}
                  {p.prix_unitaire > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--sage)' }}>
                      💵 {p.prix_unitaire.toLocaleString()} FCFA
                    </span>
                  )}
                  {marge !== null && marge > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--sage-dark)', fontWeight: 500 }}>
                      +{marge.toLocaleString()} FCFA/{p.unite}
                    </span>
                  )}
                  {(() => { const b = badgePeremption(p, lang); return b ? (
                    <span style={{ fontSize: 10, fontWeight: 600, color: b.color, background: b.bg, padding: '2px 6px', borderRadius: 50, display: 'inline-block', marginTop: 2 }}>
                      {b.icon} {b.label}
                    </span>
                  ) : null })()}
                </div>
              </div>
              <div className="product-stock">
                <div className={`stock-num ${cls}`}>{p.quantite}</div>
                <div className="stock-unit">{p.unite}</div>
                <div className="stock-actions">
                  <button className="btn-sm btn-in" onClick={e => openMove(p, 'entree', e)}>{t('entry_btn')}</button>
                  <button className="btn-sm btn-out" onClick={e => openMove(p, 'sortie', e)}>{t('exit_btn')}</button>
                </div>
              </div>
            </div>
          )
        })
      )}

      {/* ── MODAL MOUVEMENT ── */}
      {moveModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMoveModal(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">
              {moveModal.type === 'entree' ? t('stock_entry') : t('stock_exit')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>{moveModal.produit.nom}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {t('current_stock')} : <strong style={{ color: 'var(--charcoal)' }}>{moveModal.produit.quantite} {moveModal.produit.unite}</strong>
            </div>
            <div className="qty-input">
              <button className="qty-btn" onClick={() => setQty(q => Math.max(0.5, Math.round((q - 0.5) * 10) / 10))}>−</button>
              <input 
                type="number" 
                min="0.5" 
                step="0.5"
                value={qty}
                onChange={e => setQty(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
                style={{ 
                  width: 60, textAlign: 'center', border: 'none', 
                  fontFamily: 'Fraunces, serif', fontSize: 22, 
                  background: 'transparent', color: 'var(--charcoal)',
                  outline: 'none'
                }}
              />
              <button className="qty-btn" onClick={() => setQty(q => Math.round((q + 0.5) * 10) / 10)}>+</button>
            </div>
            <div className="field">
              <label>{t('note_optional')}</label>
              <input type="text" placeholder={t('entry_placeholder')} value={note} onChange={e => setNote(e.target.value)} />
            </div>
            {err && <div className="error-msg">⚠ {err}</div>}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setMoveModal(null)}>{t('cancel')}</button>
              <button className={`btn-confirm ${moveModal.type === 'sortie' ? 'danger' : ''}`} onClick={confirmMove} disabled={saving}>
                {saving ? t('saving') : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ÉDITION PRODUIT ── */}
      {editModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditModal(null)}>
          <div className="modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-handle" />
            <div className="modal-title">
              ✏️ {lang === 'fr' ? 'Modifier le produit' : 'Edit product'}
            </div>

            <div className="field">
              <label>{t('product_name')}</label>
              <input type="text" value={editModal.nom}
                onChange={e => setEditModal(m => ({ ...m, nom: e.target.value }))} />
            </div>

            {/* PRIX */}
            <div style={{ background: 'var(--sage-pale)', border: '1px solid var(--sage-light)', borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--sage-dark)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                💰 {lang === 'fr' ? 'Prix' : 'Prices'}
              </div>
              <div className="form-row">
                <div className="field">
                  <label style={{ color: 'var(--rose)' }}>🛒 {lang === 'fr' ? 'Prix d\'achat' : 'Purchase price'}</label>
                  <input type="number" min="0" value={editModal.prix_achat}
                    onChange={e => setEditModal(m => ({ ...m, prix_achat: e.target.value }))} />
                </div>
                <div className="field">
                  <label style={{ color: 'var(--sage)' }}>💵 {lang === 'fr' ? 'Prix de vente' : 'Selling price'}</label>
                  <input type="number" min="0" value={editModal.prix_unitaire}
                    onChange={e => setEditModal(m => ({ ...m, prix_unitaire: e.target.value }))} />
                </div>
              </div>

              {/* MARGE EN TEMPS RÉEL */}
              {margeEdit !== null && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: margeEdit > 0 ? 'var(--warm-white)' : 'var(--rose-pale)',
                  borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginTop: 4
                }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {lang === 'fr' ? 'Bénéfice par unité' : 'Profit per unit'}
                  </div>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: margeEdit > 0 ? 'var(--sage-dark)' : 'var(--rose)' }}>
                    {margeEdit > 0 ? '+' : ''}{margeEdit.toLocaleString()} FCFA
                    {editModal.prix_unitaire > 0 && (
                      <span style={{ fontSize: 11, marginLeft: 6, color: 'var(--muted)' }}>
                        ({Math.round((margeEdit / parseInt(editModal.prix_unitaire)) * 100)}%)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="field">
                <label>{t('unit')}</label>
                <input type="text" value={editModal.unite}
                  onChange={e => setEditModal(m => ({ ...m, unite: e.target.value }))} />
              </div>
              <div className="field">
                <label>{t('alert_threshold')}</label>
                <input type="number" min="0" value={editModal.seuil_alerte}
                  onChange={e => setEditModal(m => ({ ...m, seuil_alerte: e.target.value }))} />
              </div>
            </div>

            <div className="field">
              <label>{t('category')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {CATEGORIES.map(c => (
                  <button key={c.id} type="button"
                    onClick={() => setEditModal(m => ({ ...m, categorie: c.id }))}
                    style={{
                      padding: '6px 12px', borderRadius: 50, fontSize: 12, cursor: 'pointer',
                      border: `1.5px solid ${editModal.categorie === c.id ? 'var(--sage)' : 'var(--cream-dark)'}`,
                      background: editModal.categorie === c.id ? 'var(--sage-pale)' : 'var(--cream)',
                      color: editModal.categorie === c.id ? 'var(--sage-dark)' : 'var(--muted)',
                      fontFamily: 'DM Sans, sans-serif', fontWeight: editModal.categorie === c.id ? 600 : 400
                    }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {err && <div className="error-msg">⚠ {err}</div>}

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setEditModal(null)}>{t('cancel')}</button>
              <button className="btn-confirm" onClick={saveEdit} disabled={saving}>
                {saving ? t('saving') : (lang === 'fr' ? '✓ Enregistrer' : '✓ Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL SUPPRESSION ── */}
      {deleteModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteModal(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🗑️</div>
              <div className="modal-title" style={{ marginBottom: 4 }}>
                {lang === 'fr' ? 'Supprimer ce produit ?' : 'Delete this product?'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
                <strong style={{ color: 'var(--charcoal)' }}>{deleteModal.nom}</strong>
              </div>
            </div>
            <div style={{ background: 'var(--rose-pale)', border: '1px solid #DFA0A0', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12, color: 'var(--rose)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
              ⚠️ {lang === 'fr'
                ? 'Cette action supprimera définitivement le produit et tout son historique. Cette action est irréversible.'
                : 'This will permanently delete the product and all its history. This action cannot be undone.'}
            </div>
            {err && <div className="error-msg" style={{ marginBottom: '1rem' }}>{err}</div>}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setDeleteModal(null); setErr('') }}>
                {t('cancel')}
              </button>
              <button className="btn-confirm danger" onClick={handleDelete} disabled={deleting}>
                {deleting
                  ? (lang === 'fr' ? 'Suppression...' : 'Deleting...')
                  : (lang === 'fr' ? '🗑️ Supprimer' : '🗑️ Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}